/**
 * Gateway API Client for the Alterspective Advice Word Add-in.
 *
 * Provides typed methods for communicating with the API gateway that
 * proxies requests to Sharedo and other backend services. Uses the
 * browser fetch API with the user's session token.
 */
import type {
  AdviceHistory as SharedAdviceHistory,
  AdviceWorkflow,
  DelegateRequest,
  NotificationsResponse,
  StatusTransitionRequest,
} from '@alterspective/shared';

/** Core matter representation as returned by the gateway. */
export interface MatterSummary {
  id: string;
  reference: string;
  title: string;
  status: string;
  phase?: string;
  claimant?: MatterContact;
  defendant?: string;
  client?: MatterContact;
  partner?: MatterContact;
  claimReference?: string;
  incidentDate?: string;
  createdDate?: string;
  reportingSchema?: ReportingSchema;
}

/** Per-insurer reporting schema defining required fields and client labels. */
export interface SchemaField {
  path: string;
  label: string;
  type: string;
  required: boolean;
  blocking: boolean;
}

export interface ReportingSchema {
  version: string;
  insurer: string;
  reportTypes: Record<string, { label: string; fields: SchemaField[] }>;
}

export interface MatterContact {
  id: string;
  name: string;
  email?: string;
  organisation?: string;
}

/** Complete reserve set for a matter. */
export interface ReserveData {
  matterId: string;
  asAtDate: string;
  categories: ReserveCategory[];
  grandTotal: number;
  previousGrandTotal?: number;
}

/** A category grouping of reserve rows. */
export interface ReserveCategory {
  code: string;
  name: string;
  rows: ReserveRow[];
  subtotal: number;
}

/** A single reserve row within a category. */
export interface ReserveRow {
  accountCode: string;
  label: string;
  amount: number;
  previousAmount?: number;
}

/** Response from the finalise endpoint. */
export interface FinaliseResult {
  success: boolean;
  transactionId: string;
  message: string;
}

/** Request body for finalising reserves. */
export interface FinaliseRequest {
  changeReasonCode: string;
  transactions: FinaliseTransaction[];
  uploadDocument?: boolean;
  documentBase64?: string;
  documentFileName?: string;
  adviceTaskId?: string;
}

export interface FinaliseTransaction {
  accountCode: string;
  amount: number;
}

/** A historical advice task record from Sharedo. */
export type AdviceHistory = SharedAdviceHistory;

/** Readiness check result. */
export interface ReadinessResult {
  readinessPct: number;
  blocked: boolean;
  blockers: string[];
  missing: string[];
  updatedAt: string;
}

/** Advice workflow summary returned by the gateway. */
export type AdviceWorkflowSummary = AdviceWorkflow;

/** Health response returned by GET /api/health. */
export interface HealthStatus {
  status: string;
  service?: string;
  version: string;
  environment?: string;
  timestamp?: string;
  adapters?: Record<string, boolean>;
}

// ---------------------------------------------------------------------------
// Outlook handoff types (OH-27)
// ---------------------------------------------------------------------------

/** Request body for POST /api/v1/matter/:matterId/advice/:workflowId/outlook-handoff. */
export interface OutlookHandoffRequest {
  /** Lawyer's UPN (mailbox owner). Phase 1 takes this from `Office.context.mailbox.userProfile.emailAddress`. */
  userUpn: string;
  recipients: Array<{ name?: string; email: string }>;
  ccRecipients?: Array<{ name?: string; email: string }>;
  subjectOverride?: string;
  bodyHtmlOverride?: string;
  /** Base64-encoded .docx of the finalised advice. */
  attachmentBase64: string;
  attachmentName?: string;
  attachmentSizeBytes: number;
}

/** Response from successful handoff create. */
export interface OutlookHandoffResponse {
  draftId: string;
  graphMessageId: string;
  webLink: string;
  subscriptionEnsured: boolean;
}

/** Confirmed document–matter link (returned by POST .../documents/link). */
export interface DocumentLinkResult {
  matterId: string;
  matterReference: string;
  linkedAt: string;
  linkedBy: string;
  linkSource: 'generated' | 'manual' | 're-linked';
  confirmed: boolean;
}

/** Request body for POST .../documents/link. */
export interface DocumentLinkRequest {
  matterReference: string;
  linkedAt: string;
  linkedBy: string;
  linkSource: 'generated' | 'manual' | 're-linked';
}

/** A single AI-generated or rule-based insight about a matter. */
export interface MatterInsight {
  type: 'info' | 'warning' | 'risk' | 'suggestion';
  title: string;
  body: string;
  action?: string;
}

/** Result from the matter insights endpoint. */
export interface InsightsResult {
  matterId: string;
  question: string | null;
  generatedAt: string;
  model: 'ai' | 'rule-based';
  insights: MatterInsight[];
}

/** An admin task created for a matter. */
export interface AdminTask {
  id: string;
  matterId: string;
  typeSystemName: string;
  typeLabel: string;
  title: string;
  description?: string;
  status: 'pending' | 'completed';
  dueDate?: string;
  assignedTo?: string;
  assignedToName?: string;
  createdAt: string;
  completedAt?: string;
}

/** Request body for creating an admin task. */
export interface CreateAdminTaskRequest {
  typeSystemName: string;
  title: string;
  description?: string;
  dueDate?: string;
  assignedTo?: string;
  assignedToName?: string;
}

/** AI extraction result. */
export interface AiExtractionResult {
  success: boolean;
  reserves: ReserveRow[];
  confidence: number;
  metadata: Record<string, string>;
  model: string;
  requiresConfirmation: boolean;
}

/** Custom error class for gateway responses. */
export class GatewayError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public details?: unknown,
    public responseBody?: string,
  ) {
    super(message);
    this.name = 'GatewayError';
  }
}

/** Response from searchMatters. */
export interface SearchResult<T> {
  results: T[];
  count: number;
}

/** API Client interface. */
export interface GatewayClient {
  /** Get system health. */
  getHealth(): Promise<HealthStatus>;

  /** Get a single matter by ID. */
  getMatter(id: string): Promise<MatterSummary>;

  /** Search for matters. */
  searchMatters(query: string, limit?: number): Promise<SearchResult<MatterSummary>>;

  /** Get current reserves for a matter. */
  getReserves(matterId: string): Promise<ReserveData>;

  /** Get advice history for a matter. */
  getAdviceHistory(matterId: string): Promise<AdviceHistory[]>;

  /** Get advice readiness for a matter. */
  getReadiness(matterId: string): Promise<ReadinessResult>;

  /** Get the advice workflow status for a matter. */
  getAdviceWorkflow(matterId: string): Promise<AdviceWorkflowSummary | null>;

  /** Create a new advice workflow for a matter. */
  createAdviceWorkflow(matterId: string): Promise<AdviceWorkflowSummary>;

  /** Delegate an advice workflow to another user. */
  delegateAdvice(workflowId: string, request: DelegateRequest): Promise<AdviceWorkflowSummary>;

  /** Return a delegated advice workflow to the owner. */
  returnAdvice(workflowId: string, note?: string): Promise<AdviceWorkflowSummary>;

  /** Transition an advice workflow to a new status. */
  transitionAdvice(workflowId: string, request: StatusTransitionRequest): Promise<AdviceWorkflowSummary>;

  /** Submit finalised reserves to the gateway. */
  finaliseReserves(matterId: string, request: FinaliseRequest): Promise<FinaliseResult>;

  /** Extract reserves from a document using AI. */
  extractReserves(params: {
    matterId: string;
    firmId: string;
    clientId?: string;
    documentBase64: string;
    documentType: string;
  }): Promise<AiExtractionResult>;

  /** Suggest reserves from plain document text using AI (no upload required). */
  suggestReserves(params: {
    matterId: string;
    documentText: string;
    firmId?: string;
    clientId?: string;
  }): Promise<AiExtractionResult>;

  /** Get AI-powered or rule-based insights for a matter. */
  getMatterInsights(
    matterId: string,
    params?: { question?: string; documentText?: string },
  ): Promise<InsightsResult>;

  /** List admin tasks for a matter. */
  getAdminTasks(matterId: string): Promise<AdminTask[]>;

  /** Create a new admin task for a matter. */
  createAdminTask(matterId: string, req: CreateAdminTaskRequest): Promise<AdminTask>;

  /** Mark an admin task as completed. */
  completeAdminTask(matterId: string, taskId: string): Promise<AdminTask>;

  /** Get notifications for the current user. */
  getNotifications(): Promise<NotificationsResponse>;

  /** Mark a notification as read. */
  markNotificationRead(notificationId: string): Promise<void>;

  /** Mark all notifications as read. */
  markAllNotificationsRead(): Promise<void>;

  // OH-27: Phase 1 Outlook handoff -------------------------------------------

  /**
   * Initiate Outlook handoff after Finalise — creates the draft in the lawyer's
   * Outlook with the .docx attached and ensures a Sent Items subscription.
   * Returns the webLink so the FinalisePanel can offer "Open in Outlook".
   */
  initiateOutlookHandoff(
    matterId: string,
    workflowId: string,
    request: OutlookHandoffRequest,
  ): Promise<OutlookHandoffResponse>;

  /**
   * Cancel an active Outlook handoff — deletes the draft from Outlook + the
   * persisted draft row. Idempotent (no-op if no active handoff).
   */
  cancelOutlookHandoff(matterId: string, workflowId: string): Promise<void>;

  /**
   * Record that this document has been linked to a matter.
   * Server-side audit/confirmation endpoint — does not upload to Sharedo.
   */
  linkDocument(matterId: string, request: DocumentLinkRequest): Promise<DocumentLinkResult>;
}

/**
 * Creates a GatewayClient with the provided token and base URL.
 */
export function createGatewayClient(
  baseUrl: string,
  token: string,
  onUnauthorized?: () => void,
): GatewayClient {
  const request = async <T>(
    path: string,
    method: 'GET' | 'POST' | 'DELETE' | 'PATCH' = 'GET',
    body?: unknown,
  ): Promise<T> => {
    let response: Response;
    try {
      response = await fetch(`${baseUrl}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      throw new GatewayError(
        err instanceof Error ? err.message : 'Network request failed',
        0,
      );
    }

    if (response.status === 401 && onUnauthorized) {
      onUnauthorized();
    }

    if (!response.ok) {
      let errorMsg = `Gateway responded with ${response.status}`;
      let details: any;
      let bodyText: string | undefined;

      try {
        bodyText = await response.text();
        if (bodyText) {
          const json = JSON.parse(bodyText);
          errorMsg = json.error || json.message || errorMsg;
          details = json.details;
        }
      } catch {
        // Not JSON or error parsing
      }

      throw new GatewayError(errorMsg, response.status, details, bodyText);
    }

    if (response.status === 204) {
      return {} as T;
    }

    const text = await response.text();
    try {
      return text ? JSON.parse(text) : ({} as T);
    } catch (err) {
      throw new GatewayError('Failed to parse JSON response from gateway', response.status, undefined, text);
    }
  };

  return {
    async getHealth(): Promise<HealthStatus> {
      return request<HealthStatus>('/api/health');
    },

    async getMatter(id: string): Promise<MatterSummary> {
      return request<MatterSummary>(`/api/v1/matter/${encodeURIComponent(id)}`);
    },

    async searchMatters(query: string, limit = 20): Promise<SearchResult<MatterSummary>> {
      return request<SearchResult<MatterSummary>>(
        `/api/v1/matters/search?q=${encodeURIComponent(query)}&limit=${limit}`,
      );
    },

    async getReserves(matterId: string): Promise<ReserveData> {
      return request<ReserveData>(`/api/v1/matter/${encodeURIComponent(matterId)}/reserves`);
    },

    async getAdviceHistory(matterId: string): Promise<AdviceHistory[]> {
      return request<AdviceHistory[]>(`/api/v1/matter/${encodeURIComponent(matterId)}/advice/history`);
    },

    async getReadiness(matterId: string): Promise<ReadinessResult> {
      return request<ReadinessResult>(`/api/v1/matter/${encodeURIComponent(matterId)}/readiness`);
    },

    async getAdviceWorkflow(matterId: string): Promise<AdviceWorkflowSummary | null> {
      try {
        return await request<AdviceWorkflowSummary>(`/api/v1/matter/${encodeURIComponent(matterId)}/advice`);
      } catch (err) {
        if (err instanceof GatewayError && err.statusCode === 404) {
          return null;
        }
        throw err;
      }
    },

    async createAdviceWorkflow(matterId: string): Promise<AdviceWorkflowSummary> {
      return request<AdviceWorkflowSummary>(
        `/api/v1/matter/${encodeURIComponent(matterId)}/advice`,
        'POST',
        {},
      );
    },

    async delegateAdvice(workflowId: string, requestBody: DelegateRequest): Promise<AdviceWorkflowSummary> {
      return request<AdviceWorkflowSummary>(
        `/api/v1/advice/${encodeURIComponent(workflowId)}/delegate`,
        'POST',
        requestBody,
      );
    },

    async returnAdvice(workflowId: string, note?: string): Promise<AdviceWorkflowSummary> {
      return request<AdviceWorkflowSummary>(
        `/api/v1/advice/${encodeURIComponent(workflowId)}/return`,
        'POST',
        { note },
      );
    },

    async transitionAdvice(workflowId: string, requestBody: StatusTransitionRequest): Promise<AdviceWorkflowSummary> {
      return request<AdviceWorkflowSummary>(
        `/api/v1/advice/${encodeURIComponent(workflowId)}/transition`,
        'POST',
        requestBody,
      );
    },

    async finaliseReserves(matterId: string, requestBody: FinaliseRequest): Promise<FinaliseResult> {
      return request<FinaliseResult>(`/api/v1/matter/${encodeURIComponent(matterId)}/reserves/finalise`, 'POST', requestBody);
    },

    async extractReserves(params: {
      matterId: string;
      firmId: string;
      clientId?: string;
      documentBase64: string;
      documentType: string;
    }): Promise<AiExtractionResult> {
      return request<AiExtractionResult>('/api/v1/ai/extract-reserves', 'POST', params);
    },

    async suggestReserves(params: {
      matterId: string;
      documentText: string;
      firmId?: string;
      clientId?: string;
    }): Promise<AiExtractionResult> {
      return request<AiExtractionResult>('/api/v1/ai/suggest-reserves', 'POST', params);
    },

    // -- Insights & admin tasks --------------------------------

    async getMatterInsights(
      matterId: string,
      params?: { question?: string; documentText?: string },
    ): Promise<InsightsResult> {
      return request<InsightsResult>(
        `/api/v1/matter/${encodeURIComponent(matterId)}/insights`,
        'POST',
        params ?? {},
      );
    },

    async getAdminTasks(matterId: string): Promise<AdminTask[]> {
      const res = await request<{ tasks: AdminTask[] }>(
        `/api/v1/matter/${encodeURIComponent(matterId)}/admin-tasks`,
      );
      return res.tasks;
    },

    async createAdminTask(matterId: string, req: CreateAdminTaskRequest): Promise<AdminTask> {
      return request<AdminTask>(
        `/api/v1/matter/${encodeURIComponent(matterId)}/admin-tasks`,
        'POST',
        req,
      );
    },

    async completeAdminTask(matterId: string, taskId: string): Promise<AdminTask> {
      return request<AdminTask>(
        `/api/v1/matter/${encodeURIComponent(matterId)}/admin-tasks/${encodeURIComponent(taskId)}/complete`,
        'PATCH',
        {},
      );
    },

    // -- Notifications ----------------------------------------

    async getNotifications(): Promise<NotificationsResponse> {
      return request<NotificationsResponse>('/api/v1/notifications');
    },

    async markNotificationRead(notificationId: string): Promise<void> {
      await request<unknown>(
        `/api/v1/notifications/${encodeURIComponent(notificationId)}/read`,
        'POST',
        {},
      );
    },

    async markAllNotificationsRead(): Promise<void> {
      await request<unknown>('/api/v1/notifications/read-all', 'POST', {});
    },

    // OH-27: Outlook handoff ----------------------------------------------------

    async initiateOutlookHandoff(
      matterId: string,
      workflowId: string,
      requestBody: OutlookHandoffRequest,
    ): Promise<OutlookHandoffResponse> {
      return request<OutlookHandoffResponse>(
        `/api/v1/matter/${encodeURIComponent(matterId)}/advice/${encodeURIComponent(workflowId)}/outlook-handoff`,
        'POST',
        requestBody,
      );
    },

    async cancelOutlookHandoff(matterId: string, workflowId: string): Promise<void> {
      await request<unknown>(
        `/api/v1/matter/${encodeURIComponent(matterId)}/advice/${encodeURIComponent(workflowId)}/outlook-handoff`,
        'DELETE',
      );
    },

    async linkDocument(matterId: string, requestBody: DocumentLinkRequest): Promise<DocumentLinkResult> {
      return request<DocumentLinkResult>(
        `/api/v1/matter/${encodeURIComponent(matterId)}/documents/link`,
        'POST',
        requestBody,
      );
    },
  };
}
